import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const TOKEN_KEY = 'sentinelle_token';
const USER_KEY = 'sentinelle_user';
const ONBOARDING_KEY = 'sentinelle_onboarding_seen';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const http = axios.create({ baseURL: API_BASE, timeout: 10000 });

function readJson(key, fallback = null) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function initialsFrom(name = '') {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase()).join('') || 'US';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readJson(USER_KEY));
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(
    () => localStorage.getItem(ONBOARDING_KEY) === 'true'
  );

  // Injecter le token dans axios automatiquement
  useEffect(() => {
    if (token) {
      http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete http.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setHasSeenOnboarding(true);
  }, []);

  // Sauvegarder session en localStorage
  const saveSession = useCallback((userData, jwt) => {
    const enriched = {
      ...userData,
      initials: initialsFrom(userData.name),
      avatar: user?.avatar || null,
    };
    localStorage.setItem(TOKEN_KEY, jwt);
    localStorage.setItem(USER_KEY, JSON.stringify(enriched));
    setToken(jwt);
    setUser(enriched);
  }, [user?.avatar]);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  /**
   * Inscription — POST /api/auth/register
   */
  const register = useCallback(async ({ name, email, password, country }) => {
    setLoading(true);
    try {
      const resp = await http.post('/auth/register', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
      });
      const { token: jwt, user: userData } = resp.data;
      saveSession({ ...userData, country: country || 'Bénin' }, jwt);
      completeOnboarding();
      return { ok: true, user: userData };
    } catch (err) {
      const msg = err.response?.data?.error
        || err.response?.data?.errors?.[0]?.msg
        || 'Inscription échouée. Vérifiez vos informations.';
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [saveSession, completeOnboarding]);

  /**
   * Connexion — POST /api/auth/login
   */
  const login = useCallback(async ({ email, password }) => {
    setLoading(true);
    try {
      const resp = await http.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      });
      const { token: jwt, user: userData } = resp.data;
      // Garder le pays et l'avatar existants s'il y en avait
      saveSession({ ...userData, country: user?.country || 'Bénin' }, jwt);
      return { ok: true, user: userData };
    } catch (err) {
      const status = err.response?.status;
      let msg;
      if (status === 401) msg = 'Email ou mot de passe incorrect.';
      else if (status === 400) msg = err.response?.data?.errors?.[0]?.msg || 'Informations invalides.';
      else msg = 'Connexion impossible. Réessayez plus tard.';
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, [saveSession, user?.country]);

  /**
   * Déconnexion
   */
  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /**
   * Mise à jour du profil (local only pour le moment)
   */
  const updateProfile = useCallback((profile) => {
    const nextUser = {
      ...(user || {}),
      ...profile,
      email: (profile.email || user?.email || '').trim().toLowerCase(),
      initials: initialsFrom(profile.name || user?.name),
    };
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    setUser(nextUser);
    return nextUser;
  }, [user]);

  const updateProfilePhoto = useCallback((avatar) => updateProfile({ avatar }), [updateProfile]);

  /**
   * Obtenir le token pour les requêtes API authentifiées
   */
  const getToken = useCallback(() => token, [token]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: Boolean(user && token),
    hasSeenOnboarding,
    completeOnboarding,
    register,
    login,
    logout,
    updateProfile,
    updateProfilePhoto,
    getToken,
  }), [user, token, loading, hasSeenOnboarding, completeOnboarding, register, login, logout, updateProfile, updateProfilePhoto, getToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return context;
}
